import { inject, Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { FeedbackSubmission, FeedbackResponsePayload, FeedbackConfig } from '../models/feedback.models';
import { ApiService, GenericResponseResult } from '@brighthub/api';
import { AmplitudeService } from '@brighthub/logging';


@Injectable({
  providedIn: 'root'
})
export class FeedbackService {
  private readonly _thankYouMessages = [
    'Thanks for the feedback — we really appreciate it.',
    'Your input helps us get better. Thank you!',
    'Got it! Thanks for sharing your thoughts.',
    'Thanks — we’re always listening.',
    'Appreciate your feedback! It helps us improve.',
    "Thanks! You're helping us level up (just a little).",
    'Feedback received — you’ve officially made us smarter.',
    'Thanks for speaking up — that’s how great things happen.',
    'You made our day. Thanks for the insight!',
    'Your feedback just joined the hall of improvements.',
  ];
  private _toast: MessageService = inject(MessageService);
  private _api: ApiService = inject(ApiService);
  private _amplitude = inject(AmplitudeService);

  submitFeedback(payload: FeedbackSubmission | FeedbackConfig): Observable<FeedbackResponsePayload> {
    const body = this._toSubmission(payload);

    return this._api
      .post<FeedbackResponsePayload>('api-mobile', '/feedback', body)
      .pipe(
        map((res) => {
          if (res.result.code !== 'SERVICE_OK') {
            throw new Error(res.result.message || 'Unknown feedback submission error');
          }

          this._toast.add({
            severity: 'success',
            summary: 'Thanks!',
            detail: this._getRandomThankYouMessage(),
          });

          this._amplitude.logEvent('feedback_submitted', {
            scope: body.scope,
            metadata: body.metadata,
          });

          return res;
        }),
        catchError((err) => {
          this._toast.add({
            severity: 'error',
            summary: 'Failed to submit feedback',
            detail: err?.message || 'An unknown error occurred.',
          });
          this._amplitude.logEvent('feedback_submitted_error', {
            message: err.message || 'An unknown error occurred.',
            scope: body.scope,
            metadata: body.metadata,
            status: err.status
          });

          return throwError(() => err);
        })
      );
  }


  // Keep only the API fields; omit undefineds so we don’t send noisy keys
  private _toSubmission(input: FeedbackSubmission | FeedbackConfig): FeedbackSubmission {
    const { scope, rating, comment, userTags, metadata } = input;

    const out: FeedbackSubmission = { scope };

    if (rating !== undefined) out.rating = rating;
    if (comment !== undefined) out.comment = comment;
    if (userTags !== undefined) out.userTags = userTags;
    if (metadata !== undefined) out.metadata = metadata;

    return out;
  }

  private _getRandomThankYouMessage(): string {
    const i = Math.floor(Math.random() * this._thankYouMessages.length);
    return this._thankYouMessages[i];
  }
}
